import React, { useState, useRef, useEffect, useCallback } from 'react';
import { validateName, checkDuplicateName } from '../utils/validation';
import './InlineRenameInput.css';

/**
 * InlineRenameInput component for renaming files and folders.
 * 
 * Displays an inline text input that appears when a rename action is initiated.
 * Handles validation, keyboard interactions, and error display.
 * 
 * Requirements:
 * - 2.1: Display inline edit field with current folder name for folder rename
 * - 3.1: Display inline edit field with current file name for file rename
 * 
 * @param {Object} props
 * @param {string} props.currentName - The current name of the file/folder being renamed
 * @param {Function} props.onSubmit - Callback when rename is submitted with valid name
 * @param {Function} props.onCancel - Callback when rename is cancelled
 * @param {Array<string|Object>} [props.existingNames=[]] - Array of existing names for duplicate checking
 * @param {string} [props.className=''] - Additional CSS class names
 * @param {string} [props.placeholder='Enter name'] - Placeholder text for the input
 */
const InlineRenameInput = ({
  currentName,
  onSubmit,
  onCancel,
  existingNames = [],
  className = '',
  placeholder = 'Enter name'
}) => {
  const [value, setValue] = useState(currentName || '');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  /**
   * Auto-focus the input and select all text when mounted.
   * This allows users to easily replace the entire name by typing.
   */
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  /**
   * Validates the input value against name rules and duplicate checking.
   * Excludes the current name from duplicate checking to allow "no change" submissions.
   * 
   * @param {string} name - The name to validate
   * @returns {Object} ValidationResult with valid boolean and optional error message
   */
  const validate = useCallback((name) => {
    // First validate the name format
    const nameValidation = validateName(name);
    if (!nameValidation.valid) {
      return nameValidation;
    }

    // Filter out the current name from existing names for duplicate checking
    // This allows the user to submit the same name (no change)
    const filteredExistingNames = existingNames.filter(item => {
      const itemName = typeof item === 'string' 
        ? item 
        : (item.name || item.key?.split('/').pop() || '');
      return itemName.toLowerCase() !== currentName.toLowerCase();
    });

    // Check for duplicates
    return checkDuplicateName(name, filteredExistingNames);
  }, [existingNames, currentName]);

  /**
   * Handles input value changes.
   * Clears any existing error when the user starts typing.
   * 
   * @param {Event} e - The input change event
   */
  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  /**
   * Handles form submission.
   * Validates the input and calls onSubmit if valid.
   * 
   * @param {Event} [e] - The form submit event
   */
  const handleSubmit = useCallback((e) => {
    if (e) {
      e.preventDefault();
    }

    if (isSubmitting) {
      return;
    }

    const trimmedValue = value.trim();
    
    // Validate the name
    const validation = validate(trimmedValue);
    
    if (!validation.valid) {
      setError(validation.error);
      // Focus the input so user can correct the error
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
      return;
    }

    // If the name hasn't changed, just cancel
    if (trimmedValue === currentName) {
      onCancel();
      return;
    }

    setIsSubmitting(true);
    onSubmit(trimmedValue);
  }, [value, validate, currentName, onSubmit, onCancel, isSubmitting]);

  /**
   * Handles keyboard events.
   * - Enter: Submit the form
   * - Escape: Cancel the rename
   * 
   * @param {KeyboardEvent} e - The keyboard event
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }, [handleSubmit, onCancel]);

  /**
   * Handles blur event.
   * When the input loses focus, we validate and submit if valid,
   * or cancel if the value is unchanged or invalid.
   */
  const handleBlur = useCallback(() => {
    // Small delay to allow click events on buttons to fire first
    setTimeout(() => {
      if (isSubmitting) {
        return;
      }

      const trimmedValue = value.trim();
      
      // If unchanged, cancel
      if (trimmedValue === currentName) {
        onCancel();
        return;
      }

      // Validate and submit if valid
      const validation = validate(trimmedValue);
      if (validation.valid) {
        setIsSubmitting(true);
        onSubmit(trimmedValue);
      } else {
        // Show error but don't cancel - let user fix it
        setError(validation.error);
      }
    }, 150);
  }, [value, currentName, validate, onSubmit, onCancel, isSubmitting]);

  return (
    <div className={`inline-rename-input ${className}`}>
      <form onSubmit={handleSubmit} className="inline-rename-form">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`inline-rename-field ${error ? 'inline-rename-field-error' : ''}`}
          aria-label="Rename input"
          aria-invalid={!!error}
          aria-describedby={error ? 'rename-error' : undefined}
          disabled={isSubmitting}
          autoComplete="off"
          spellCheck="false"
        />
        {error && (
          <div 
            id="rename-error" 
            className="inline-rename-error" 
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </div>
  );
};

export default InlineRenameInput;
